import { TransitionScreen } from '@components/transition';
import { Organization, useGetOrganizationsQuery } from '@generated/graphql';
import { agentPanelOpenAtom } from '@hooks/agentPanelAtoms';
import { hiddenOrganizationIdsAtom } from '@hooks/userPreferencesAtoms';
import { useAtom } from 'jotai';
import { PropsWithChildren, createContext, useContext, useEffect, useMemo } from 'react';
import { useCookie } from 'react-use';

type OrganizationContextType = {
  organizationId?: string;
  organization?: Organization;
  organizations?: Organization[];
  isAdmin: boolean;
  isMember: boolean;
};

const OrganizationContext = createContext<OrganizationContextType>({
  organizationId: undefined,
  organization: undefined,
  organizations: undefined,
  isAdmin: false,
  isMember: false,
});

export const OrganizationContextProvider = ({
  organizationId,
  children,
}: PropsWithChildren<{ organizationId?: string }>) => {
  const [lastOrganizationId, setLastOrganizationId] = useCookie('lastOrganizationId');
  const [hiddenOrganizationIds] = useAtom(hiddenOrganizationIdsAtom);
  const [, setAgentPanelOpen] = useAtom(agentPanelOpenAtom);

  const { data, loading } = useGetOrganizationsQuery();

  // Validate that the organization from cookie is actually in user's org list
  const isLastOrgValid =
    lastOrganizationId &&
    data?.getOrganizations?.some((org) => org.organizationId === lastOrganizationId);

  // Determine the current organization ID
  // Priority: URL param > validated cookie > first non-hidden org
  let currentOrganizationId = organizationId || (isLastOrgValid ? lastOrganizationId : undefined);

  // If no valid org ID, default to first non-hidden org
  if (!currentOrganizationId && data?.getOrganizations) {
    const firstVisibleOrg = data.getOrganizations.find(
      (org) => !hiddenOrganizationIds.includes(org.organizationId),
    );
    currentOrganizationId = firstVisibleOrg?.organizationId;
  }

  // Update cookie with current valid org ID
  if (
    currentOrganizationId &&
    (!lastOrganizationId || lastOrganizationId !== currentOrganizationId)
  ) {
    void setLastOrganizationId(currentOrganizationId);
  }

  const organization = data?.getOrganizations?.find(
    (org) => org.organizationId === currentOrganizationId,
  );

  const value = useMemo(() => {
    return {
      organizationId: currentOrganizationId,
      organization,
      organizations: data?.getOrganizations,
      isAdmin: organization?.role === 'admin',
      isMember: organization?.role === 'member',
    };
  }, [currentOrganizationId, organization, data?.getOrganizations]);

  useEffect(() => {
    if (!loading && organizationId && data?.getOrganizations) {
      const hasAccess = data.getOrganizations.some((org) => org.organizationId === organizationId);
      if (!hasAccess) {
        window.location.href = '/';
      }
    }
  }, [loading, organizationId, data?.getOrganizations]);

  // Hide agent chat panel if not in agent org
  useEffect(() => {
    if (organization && !organization.serviceOnly) {
      setAgentPanelOpen(false);
    }
  }, [organization, setAgentPanelOpen]);

  if (loading) {
    return <TransitionScreen />;
  }

  if (organizationId && !organization) {
    return <TransitionScreen />;
  }

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganizationContext = () => {
  return useContext(OrganizationContext);
};
